/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  gql,
  InMemoryCache,
  NextLink,
  Operation,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
// @ts-expect-error - No typing
import apolloLogger from 'apollo-link-logger';
import { GraphQLError } from 'graphql';
import { cloneDeep, has } from 'lodash';

import * as K from 'common/constants';
import history from 'service/customHistory';
import { TEAM_TOKEN_QUERY } from 'common/queries/apolloQueries';
import logger from 'common/ScalyrLogger';
import { Maybe, ResultData } from 'common/graphqlTypes';
import { buildAbsoluteUrl, getUrlParam, isTeamTokenClearingPage, removeParamsFromUrl } from 'common/util/location';

import { isDebug } from 'components/common/Managers/DebugManager.state';
import { redirectToLocationWithTeamToken } from 'components/common/Managers/RouteStateManager.utils';
import { PubSub } from 'service/PubSub';
import { Settings } from 'common/types';
import { applySettingsToUrl } from 'common/functions';

type Detail = {
  field: string;
  message: string;
};

type UnauthenticatedError = {
  details: Array<Detail>;
};

export type NetworkErrorType = {
  operation: string;
  error: Error | ServerError | ServerParseError | undefined;
};

// Headers used in ApolloLink functions
enum HEADER {
  SCALYR_TEAM_TOKEN = 'scalyr-team-token',
  X_CSRF_TOKEN = 'X-Csrf-Token',
  X_DATASET_QUERY_FORWARD_TAG = 'X-Dataset-Query-Forward-Tag',
}

export const networkErrorObject = new PubSub<NetworkErrorType | undefined>(undefined);

export const settingsObj = new PubSub<Settings>(K.DEFAULT_SETTINGS);

export const NUMBER_OF_RETRYING_ATTEMPTS = 2;

const SWITCH_TEAMS_OP = 'switchteams';

// A map (Record) that tracks the appropriate forward tag for server routing of pingQuery and removeQuery operations.
// The Record keys are the tokens returned in launchQuery responses, which uniquely identify an LRQ.
const lrqTokenToForwardTag: Record<string, string> = {};

const customFetch = async (uri: RequestInfo, options: RequestInit): Promise<Response> => {
  const { operationName } = JSON.parse(options.body as string);
  const response = await fetch(`${uri}?opname=${operationName}`, options);
  // Apollo Client throws a JSON parse error when it receives an HTML page (usually on error pages)
  // https://github.com/apollographql/apollo-feature-requests/issues/153#issuecomment-851092478
  if (response.status >= 500) {
    return Promise.reject(response.status);
  }
  return response;
};

const httpLink = createHttpLink({
  uri: K.API_ENDPOINT,
  fetch: customFetch,
  credentials: 'same-origin',
});

// a list of query/mutation to retry if there is a network error.
const retryOperations = [K.LOG_TO_SCALYR];
const inMemoryCache = new InMemoryCache({
  possibleTypes: {
    ResultData: ['FacetData', 'GenericResultData', 'HistogramData', 'LogEntryData', 'PlotData', 'PqResultData'],
  },
  typePolicies: {
    QueryRequestResult: {
      fields: {
        data: {
          // Custom merge because the server returns null when data has not changed to save on network bytes.
          merge(existing: Maybe<ResultData> | undefined, incoming: Maybe<ResultData>, { mergeObjects }): any {
            // We use a semantic null to indicate that there is no data yet
            if (!existing && !incoming) {
              return null;
            }
            // If incoming is null, take an exact copy of existing instead. existing is frozen with Object.freeze in
            // dev mode. Apollo will throw an error if we allow any references to existing in incomingObj.
            const incomingObj = incoming || cloneDeep(existing);
            const existingObj = existing || {};
            return mergeObjects(existingObj, incomingObj);
          },
        },
      },
    },
    LogEntryEdge: {
      keyFields: ['continuationToken'],
    },
    AppState: {
      keyFields: false,
    },
  },
});

const INITIALIZATION_QUERY = gql`
  {
    ClientAccountState @client {
      teamTokenMismatch @client
    }
    AppState @client
  }
`;

inMemoryCache.writeQuery({
  query: INITIALIZATION_QUERY,
  data: {
    ClientAccountState: {
      teamTokenMismatch: null,
      __typename: 'ClientAccountState',
    },
    AppState: {
      __typename: 'AppState',
    },
  },
});

/**
 * Middleware for adding the teamToken, csrfToken, and/or server routing forward tag as request headers.
 *
 * Teamtoken is only passed as a request header if it is present as a URL query string param.
 * If the token does not match the user's current team, API will throw an error for the client to catch (see errorLink).
 * If a user navigates to a route manually (not typing out the team token), then we will wait for the current teamToken
 * in the response header from the server and add it to the URL for all subsequent requests (see afterwareLink below).
 *
 * The csrfToken is required for any mutation call. The server injects this token on to the window.
 * If it is not present, the request will fail.
 *
 * The server routing forward tag must be appended as a request header to pingQuery and removeQuery operations, if it
 * was returned in the response headers for the corresponding launchQuery operation.
 *
 * ApolloLink takes in a callback function that expects parameters:
 * @param operation     The operation being passed through the link.
 * @param forward       Specifies the next link in the chain of links
 *
 * For further documentation: https://www.apollographql.com/docs/link/overview/
 */
const middlewareLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  const searchParams = new URLSearchParams(window.location.search);
  const teamToken = searchParams.get('teamToken');

  // Don't add the team token header if this is a request to change teams or if the token isn't present in the URL.
  // We will wait for the successful team switch, then set the URL param in the afterware link for all future requests.
  if (operation?.operationName?.toLowerCase() !== SWITCH_TEAMS_OP && teamToken) {
    operation.setContext(({ headers = {} }: Record<string, any>) => ({
      headers: {
        ...headers,
        [HEADER.SCALYR_TEAM_TOKEN]: teamToken,
      },
    }));
  }
  const csrfToken = window.xsrfToken ?? '';

  if (csrfToken) {
    operation.setContext(({ headers = {} }: Record<string, any>) => ({
      headers: {
        ...headers,
        [HEADER.X_CSRF_TOKEN]: csrfToken,
      },
    }));
  }

  const lrqToken = operation?.variables?.token;
  const forwardTag = lrqTokenToForwardTag[lrqToken];
  if (forwardTag && ['pingQuery', 'removeQuery'].includes(operation?.operationName)) {
    operation.setContext(({ headers = {} }: Record<string, any>) => ({
      headers: {
        ...headers,
        [HEADER.X_DATASET_QUERY_FORWARD_TAG]: forwardTag,
      },
    }));
    // In the removeQuery case, clean up the entry to prevent a memory leak
    if (operation?.operationName === 'removeQuery') {
      delete lrqTokenToForwardTag[lrqToken];
    }
  }
  return forward(operation);
});

/**
 * Helper function to add the team Token mismatch data to the cache if necessary.
 *
 * @param errorPayload    The payload from the API associated with the particular error.
 * @param cache           The apollo cache from operation.getContext() in the middleware function.
 */
const handleTokenMismatch = (errorPayload: Record<string, any>, cache: any): void => {
  if (errorPayload.actual) {
    const { ClientAccountState } = cache.readQuery({
      query: TEAM_TOKEN_QUERY,
    });

    // If mismatched token data already exists in the cache, no need for the other graphQL requests to complain about it.
    // We need user to take action to get rid of the mismatch error.
    if (!ClientAccountState.teamTokenMismatch) {
      const requested = errorPayload.requested ? { ...errorPayload.requested, __typename: 'RequestedTeam' } : null;

      const teamTokenData = {
        requested,
        message: errorPayload.message,
        actual: {
          ...errorPayload.actual,
          __typename: 'ActualTeam',
        },
        __typename: 'TeamTokenManager',
      };

      cache.writeQuery({
        query: TEAM_TOKEN_QUERY,
        data: {
          ClientAccountState: {
            teamTokenMismatch: teamTokenData,
            __typename: 'AccountDataFetcher',
          },
        },
      });
    }
  }
};

const isServerError = (error: unknown): error is ServerError => has(error, 'result') && has(error, 'statusCode');

const isUnauthorizedOrForbidden = ({ statusCode }: ServerError): boolean => [401, 403].includes(statusCode);

const isNotLogging = ({ operationName }: Operation): boolean => operationName !== K.LOG_TO_SCALYR;

const userIsUnauthenticated = (graphQLErrors: readonly GraphQLError[]): boolean => {
  // Server response is not following the GraphQLError type.
  const firstError = graphQLErrors?.[0] as unknown as UnauthenticatedError;
  const firstErrorDetails = firstError?.details?.[0];

  return firstErrorDetails?.field === 'status' && firstErrorDetails?.message === 'unauthenticated';
};

/**
 * Middleware for handling general errors received from the apollo client.
 * Currently only using this to catch team token mismatch errors.

 * onError takes a callback function and we are destructuring these parameters:
 * @param graphQLErrors   An array of errors from the GraphQL endpoint
 * @param networkError    Any error during the link execution or server response
 *                        that wasn't delivered as part of the errors field in the
 *                        GraphQL result.
 * @param operation       The operation being passed through the link.
 *
 * For further documentation: https://www.apollographql.com/docs/link/links/error/
 */
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    if (userIsUnauthenticated(graphQLErrors) && isNotLogging(operation)) {
      const settings = settingsObj.get();

      // If user authentication expired, redirect to the login page
      // after re-authenticating user will be redirected back to the same page
      const originalUrl = buildAbsoluteUrl(removeParamsFromUrl(window.location, [K.URL_PARAM.TEAM_TOKEN]));
      const originalUrlWithSettings = applySettingsToUrl(originalUrl, settings);
      window.location.href = `${K.ROUTE.LOG_IN}?${K.URL_PARAM.AUTH_ORIGINAL_URL}=${originalUrlWithSettings}`;
    }
    // Add some general error catching here. For graphQLErrors
    graphQLErrors.map(({ message, locations, path }) =>
      logger.debug(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
    );
  }

  if (
    networkError &&
    isServerError(networkError) &&
    isUnauthorizedOrForbidden(networkError) &&
    isNotLogging(operation)
  ) {
    const { cache } = operation.getContext();
    handleTokenMismatch(networkError.result, cache);
  }

  if (networkError && isNotLogging(operation)) {
    networkErrorObject.publish({ error: networkError, operation: operation.operationName });
  }
});

/**
 * Afterware that reads the teamToken and/or server routing forward tag from the response headers.
 *
 * We update the URL param with the teamToken value from the server unless it is already present,
 * or unless we are on certain pages that allow the user to clear this value.
 *
 * If are processing the response to a launchQuery request and there is a server routing forward tag in the
 * response headers, we update the lrqTokenToForwardTag Record for this lrqToken with the value of the header.
 *
 * ApolloLink takes in a callback function that expects parameters:
 * @param operation   The operation being passed through the link.
 * @param forward     Specifies the next link in the chain of links
 *
 * For further documentation: https://www.apollographql.com/docs/link/overview/
 */
const afterwareLink = new ApolloLink((operation: Operation, forward: NextLink) =>
  forward(operation).map((response) => {
    if (networkErrorObject.get() && isNotLogging(operation)) {
      networkErrorObject.publish(undefined);
    }
    const {
      response: { headers },
    } = operation.getContext();
    if (headers && !isTeamTokenClearingPage(window?.location?.pathname)) {
      const teamToken = headers.get(HEADER.SCALYR_TEAM_TOKEN);

      if (teamToken && teamToken !== getUrlParam(window.location.search, K.URL_PARAM.TEAM_TOKEN)) {
        redirectToLocationWithTeamToken(history, teamToken);
      }
    }
    const forwardTag = headers?.get(HEADER.X_DATASET_QUERY_FORWARD_TAG);
    const lrqToken = response?.data?.launchQuery?.token;
    if (lrqToken && forwardTag) {
      lrqTokenToForwardTag[lrqToken] = forwardTag;
    }

    return response;
  }),
);

/**
 * retryLink re-attempts query or mutation for network errors (not for graphQL errors yet).
 *
 * For further documentation: https://www.apollographql.com/docs/link/links/retry/
 */
const retryLink = new RetryLink({
  delay: {
    initial: 1000,
    max: Infinity,
    jitter: true,
  },
  attempts: (count, operation, error): boolean =>
    retryOperations.includes(operation.operationName) && count <= NUMBER_OF_RETRYING_ATTEMPTS && !!error,
});

const apolloLoggerLink = ApolloLink.split(
  isDebug,
  apolloLogger,
  new ApolloLink((operation, forward) => forward(operation)),
);

const client = new ApolloClient({
  link: ApolloLink.from([apolloLoggerLink, retryLink, errorLink, afterwareLink, middlewareLink, httpLink]),
  cache: inMemoryCache,
  assumeImmutableResults: true, // enables perf. optimization https://github.com/apollographql/apollo-client/blob/c6a1c32f2989cdf841d8a32fd2bbb63b774acb4c/CHANGELOG.md#apollo-client-260-1
});

export default client;
